import { Injectable, NgZone, signal } from '@angular/core';
import { SampleDataType } from 'user.interface';
import { LanguageService } from './language.service';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { AdobeAnalyticsService, EncryptionKeyService, Logger } from '@pfizer/newton-jetpack-utils';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { UserStore } from 'app/stores/user.store';
import { FirebaseService } from './firebase.service';
import { MenuNavigationService } from './menu-navigation.service';
import { RecallService } from './recall.service';
import { StorageService } from './storage.service';
import { VideoDownloadService } from './video-download.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { environment } from 'environments/environment';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class BootstrapService {
  private readonly isInitialized = signal(false);
  constructor(public langService: LanguageService,
    private readonly logger: Logger,
    public platform: Platform,
    public translate: TranslateService,
    public userStore: UserStore,
    public videoDownloadService: VideoDownloadService,
    public storageService: StorageService,
    public encryptionService: EncryptionKeyService,
    public analytics: AdobeAnalyticsService,
    public ngZone: NgZone,
    public firebaseService: FirebaseService,
    public recallService: RecallService,
    public router: Router,
    protected menuNavigationService: MenuNavigationService,
    protected utilService: UtilsService
  ) {}

  public async initializeApp(): Promise<SampleDataType> {
    if (Capacitor.isNativePlatform()) {
      const appInfo = await App.getInfo();
      window['appUrl'] = appInfo.id;
    }

    return this.langService.determineUserLanguage();
  }

  public async init() {
    if (this.isInitialized()) {
      this.logger.warn('[BootstrapService] Trying to initialize more than once');

      return;
    }

    this.isInitialized.set(true);

    try {
      await this.platform.ready();
      this.setStatusBarColor();
      await this.storageService.initDatabase(await this.encryptionService.getKey());
      await this.userStore.initStore();

      this.syncVideos();
      this.analyticsConfiguration();
      this.subscribeToAppResumeEvents();

    } catch (e) {
      // catch and log so that the init sequence can continue and let the app recover itself afterwards
      this.logger.error('[BootstrapService]', JSON.stringify(e));
    }
  }

  protected analyticsConfiguration() {
    this.analytics.configure({
      appName: environment.analyticsAppName,
      trackContentPrefix: environment.trackContentPrefix,
      templateName: environment.templateName,
      platform: Capacitor.getPlatform()
    });
    this.analytics.setDebugLogging(true);
  }

  private subscribeToAppResumeEvents() {
    this.platform.pause.subscribe(async () => {
      await this.firebaseService.refresh();
    });
  }

  private async setStatusBarColor() {
    if (this.platform.is('capacitor') && this.platform.is('ios')) {
      await StatusBar.setStyle({ style: Style.Light });
    }
  }

  private async syncVideos() {
    const platfrm = Capacitor.getPlatform();
    if (platfrm === 'web') {
      return;
    }
    await this.videoDownloadService.downloadAllVideos();
  }

}
